import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Message from 'components/Message';
import { openModal } from 'components/Modal';
import { PFE_ICONS } from 'constants/icons';
import { schedulePropTypes } from 'constants/propTypes';
import { selectActiveVenue } from 'selectors/general';

import { actionsMessages } from '../ScheduleDetail/ScheduleDetail.messages';
import { emailScheduleAttendees } from '../YieldManagement.saga';
import ActionsDropdown from './ActionsDropdown';
import { countReservations, getActionWindows } from './ScheduleDetailV2.helpers';
import { useScheduleRoster } from './ScheduleDetailV2.hooks';
import messages from './ScheduleDetailV2.messages';

const EmailActionsMenu = ({ schedule }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { venueName, venueSubtitle } = useSelector(selectActiveVenue);
  const { data: roster } = useScheduleRoster(schedule.scheduleId);

  const { canPerformScheduleActions, isInExtendedActionsWindow } = getActionWindows(schedule);
  if (schedule.isDisabled || (!canPerformScheduleActions && !isInExtendedActionsWindow)) {
    return null;
  }

  const { newReservationsCount, reservationsCount } = countReservations(roster);

  const openEmailModal = ({ newAttendeesOnly, reservations, title }) =>
    dispatch(
      openModal('EmailModal', {
        onSubmit: (...args) => dispatch(emailScheduleAttendees(...args)),
        title,
        to: <Message message={actionsMessages.toContent} values={{ reservations }} />,
        subject: (
          <Message
            message={actionsMessages.subjectContent}
            values={{
              startDateTime: schedule.startDateTime,
              venueNameSubtitle: `${venueName}${venueSubtitle ? ` (${venueSubtitle})` : ''}`,
            }}
          />
        ),
        initialValues: {
          schedule,
          newAttendeesOnly,
          message: '',
        },
      })
    );

  const items = [
    {
      key: 'email-class',
      label: <Message message={messages.emailClass} />,
      onSelect: () =>
        openEmailModal({
          newAttendeesOnly: false,
          reservations: reservationsCount,
          title: <Message message={messages.emailClass} />,
        }),
    },
    newReservationsCount > 0 &&
      !isInExtendedActionsWindow && {
        key: 'email-new-clients',
        label: <Message message={messages.emailNewClients} values={{ count: newReservationsCount }} />,
        onSelect: () =>
          openEmailModal({
            newAttendeesOnly: true,
            reservations: newReservationsCount,
            title: <Message message={messages.emailNewClients} values={{ count: newReservationsCount }} />,
          }),
      },
  ].filter(Boolean);

  return (
    <ActionsDropdown
      dataCypress="schedule-detail-v2-email-menu"
      frosted={reservationsCount === 0}
      icon={PFE_ICONS.envelopeOutline}
      items={items}
      label={intl.formatMessage(messages.emailMenuLabel)}
    />
  );
};

EmailActionsMenu.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes),
};

export default EmailActionsMenu;
